export interface CarrierSummary {
  insuranceCarrierId: number;
  carrierName: string;
  totalForms: number;
  totalStates: number;
}

export interface FormListItem {
  id: string;
  formId: string;
  title: string;
  displayName: string;
  formType: string;
  stateCode: string | null;
  product: string;
  status: string;
  updatedAt: string;
}

export type SelectedItemType = 'section' | 'subsection' | 'group' | 'question';

export interface SelectedItem {
  type: SelectedItemType;
  id: string;
  sectionId?: string;
  subsectionId?: string;
  groupId?: string;
  questionRef?: string;
}

export interface FormBuilderState {
  formDefinition: PolicyFormDefinition;
  formMetadata: PolicyFormMetadata;
  selectedItem: SelectedItem | null;
  isDirty: boolean;
}

export const QUESTION_TYPES: { value: PolicyFormQuestion['type']; label: string }[] = [
  { value: 'text', label: 'Short Text' },
  { value: 'number', label: 'Number' },
  { value: 'select', label: 'Dropdown' },
  { value: 'radio', label: 'Single Choice' },
  { value: 'date', label: 'Date' },
  { value: 'textarea', label: 'Long Text' },
  { value: 'multiselect', label: 'Multiple Choice' },
  { value: 'table', label: 'Table' },
  { value: 'textselect', label: 'Short Text + Dropdown' },
  { value: 'textarea-text', label: 'Long Text + Short Text' },
  { value: 'textarea-append', label: 'Long Text (Appendable)' },
];

export const VALIDATION_TYPES: {
  value: NonNullable<PolicyFormQuestion['validationType']>;
  label: string;
}[] = [
  { value: 'height', label: 'Height' },
  { value: 'weight', label: 'Weight' },
  { value: 'age', label: 'Age' },
  { value: 'address', label: 'Address' },
  { value: 'phone', label: 'Phone' },
  { value: 'email', label: 'Email' },
  { value: 'currency', label: 'Dollar Amount' },
  { value: 'ssn', label: 'Social Security Number' },
  { value: 'ssn4Digit', label: 'Last 4 of SSN' },
  { value: 'zipcode', label: 'Zip Code' },
  { value: 'streetAddress', label: 'Street Address' },
  { value: 'city', label: 'City' },
  { value: 'state', label: 'State' },
  { value: 'npn', label: 'National Producer Number (NPN)' },
  { value: 'numberOnly', label: 'Numbers Only' },
  { value: 'cityStateZipcode', label: 'City, State & Zip Code' },
  { value: 'driverLicense', label: "Driver's License" },
];

export const COMPARISON_OPERATORS: { value: ComparisonOperator; label: string }[] = [
  { value: 'equals', label: 'Is equal to' },
  { value: 'notEquals', label: 'Is not equal to' },
  { value: 'contains', label: 'Contains' },
  { value: 'notContains', label: 'Does not contain' },
  { value: 'empty', label: 'Is empty' },
  { value: 'notEmpty', label: 'Is not empty' },
  { value: 'in', label: 'Is one of' },
  { value: 'notIn', label: 'Is not one of' },
  { value: 'greaterThan', label: 'Is greater than' },
  { value: 'greaterThanOrEqual', label: 'Is greater than or equal to' },
  { value: 'lessThan', label: 'Is less than' },
  { value: 'lessThanOrEqual', label: 'Is less than or equal to' },
];

export const GROUP_FORMATS: {
  value: NonNullable<PolicyFormQuestionGroup['format']>;
  label: string;
}[] = [
  { value: 'default', label: 'Stacked (Default)' },
  { value: 'grid', label: 'Side by Side (2 Columns)' },
  { value: 'inline', label: 'Single Row' },
];

export const GROUP_ALIGNMENTS: {
  value: NonNullable<PolicyFormQuestionGroup['align']>;
  label: string;
}[] = [
  { value: 'start', label: 'Left' },
  { value: 'center', label: 'Center' },
  { value: 'end', label: 'Right' },
];

export const COMPOSITE_DELIMITERS: {
  value: CompositeDelimiter;
  label: string;
}[] = [
  { value: ' ', label: 'Space ( )' },
  { value: ',', label: 'Comma (,)' },
  { value: '-', label: 'Dash (-)' },
  { value: ', ', label: 'Comma + Space (, )' },
];

export const TABLE_COLUMN_TYPES: {
  value: PolicyFormTableColumn['type'];
  label: string;
}[] = [
  { value: 'text', label: 'Short Text' },
  { value: 'number', label: 'Number' },
  { value: 'date', label: 'Date' },
  { value: 'radio', label: 'Single Choice' },
  { value: 'select', label: 'Dropdown' },
  { value: 'textselect', label: 'Short Text + Dropdown' },
];

export const TEMPLATE_CONDITIONAL_OPERATORS: {
  value: TemplateConditionalRule['operator'];
  label: string;
}[] = [
  { value: 'equals', label: 'Is equal to' },
  { value: 'notEquals', label: 'Is not equal to' },
  { value: 'contains', label: 'Contains' },
  { value: 'notContains', label: 'Does not contain' },
  { value: 'empty', label: 'Is empty' },
  { value: 'notEmpty', label: 'Is not empty' },
  { value: 'in', label: 'Is one of' },
  { value: 'notIn', label: 'Is not one of' },
  { value: 'greaterThan', label: 'Is greater than' },
  { value: 'greaterThanOrEqual', label: 'Is greater than or equal to' },
  { value: 'lessThan', label: 'Is less than' },
  { value: 'lessThanOrEqual', label: 'Is less than or equal to' },
];

export const TEMPLATE_CONDITIONAL_LOGIC: { value: TemplateConditional['logic']; label: string }[] =
  [
    { value: 'AND', label: 'All conditions must match (AND)' },
    { value: 'OR', label: 'Any condition can match (OR)' },
  ];

export const TABLE_COLUMN_VALIDATION_TYPES: {
  value: NonNullable<PolicyFormTableColumn['validationType']>;
  label: string;
}[] = [
  { value: 'phone', label: 'Phone' },
  { value: 'email', label: 'Email' },
  { value: 'ssn', label: 'Social Security Number' },
  { value: 'ssn4Digit', label: 'Last 4 of SSN' },
  { value: 'zipcode', label: 'Zip Code' },
  { value: 'streetAddress', label: 'Street Address' },
  { value: 'city', label: 'City' },
  { value: 'state', label: 'State' },
  { value: 'npn', label: 'National Producer Number (NPN)' },
  { value: 'numberOnly', label: 'Numbers Only' },
  { value: 'driverLicense', label: "Driver's License" },
];
